<template>
  <div class="card">
    <h6>YOUR HOME</h6>
    <div class="form-group _margin-bottom-30">
      <div class="col-sm-7">
        <h4>Your postcode</h4>
        <validation-messages label="Postcode" :$v="$v.postcode">
          <div class="input-group right">
            <form-field id="postcode" type="nospace" v-model="quote.postcode" :error="$v.postcode.$error"
              @change="onReset(); $refs.address.onReset()" />

            <div class="input-group-btn">
              <uiv-btn type="primary" :disabled="addressLoader || addressFound" @click="onGetAddress(quote.postcode)">
                <loader :show="addressLoader" />Search
              </uiv-btn>
            </div>
          </div>
        </validation-messages>
      </div>
    </div>

    <div class="form-group" v-show="addressList.length && !addressFound">
      <div class="col-sm-7">
        <form-label id="searchAddress" v-show="!selectedAddress">Select your address</form-label>

        <validation-messages label="Address" :$v="$v.address">
          <search-select-list ref="address" id="searchAddress" :select-list="addressList" label-key="text"
            :error="$v.address.$error" @onChoose="onChoose" @onReset="onReset">
            {{ selectedAddress }}
          </search-select-list>
        </validation-messages>
      </div>
    </div>

    <div class="form-group" v-show="addressFound">
      <div class="col-sm-7">
        <div class="validation-messages">
          <div>
            <div class="search-selected-item">
              <p> {{ selectedAddress }} </p>
              <div class="text-right"><button type="button" disabled="disabled"
                  class="btn disabled btn-ghost btn-xs">Change</button></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="_margin-top-20 _margin-bottom-10">
      <small>
        I have read the <a :href="appSettings.supplierTermsAndConditions" target="_blank">Terms & Conditions</a> / <a
          class="padding-left" :href="appSettings.supplierPrivacyPolicy" target="_blank">Privacy Policy</a>
      </small>
    </div>

    <small>
      By continuing, you grant access to your gas or electricity meter information from the national energy industry
      database. This access is necessary to provide you with accurate quotes.
    </small>

    <uiv-alert type="danger" v-if="selectedAddress && !xoserveMetersLoader && $v.address.domesticMeter == false">
      <b>Non Domestic Meter Type Identified!</b> <br />
      The address you have selected appears to be a non domestic energy metered property. We are unable to provide
      quotes for these meter types
      <div class="row _margin-top-20" v-if="!appSettings.whiteLabel">
        <div class="col-xs-6 col-md-4">
          <small><b>Call Us</b></small><br />
          {{ appSettings.nonDomesticMeterPhone }}
        </div>

        <div class="col-xs-6 col-md-8">
          <small><b>Opening hours</b></small><br />
          {{ appSettings.nonDomesticMeterPhoneOpenHours }}
        </div>
      </div>
    </uiv-alert>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'YourHome',
  data() {
    return {
      selectedAddress: null,
      noMpanOrMprn: false,
      ofgemAverageGasList: [],
      ofgemAverageElecList: []
    };
  },
  props: {
    $v: Object,
    addressFound: Boolean,
  },
  computed: {
    ...mapGetters({
      is02Meter: 'quote/is02Meter',
    }),
    ...mapState({
      isQuoteParamsJourney: ({ session }) => session.quoteParams.isQuoteParamsJourney,
      appSettings: ({ app_settings }) => app_settings,
      defaultSupplierId: ({ app_settings }) => app_settings.defaultSupplierId,
      quote: ({ quote }) => quote,
      addressList: ({ lookups }) => lookups.addresses,
      addressLoader: ({ lookups }) => lookups.requesting.addresses,
      xoserveMetersLoader: ({ lookups }) => lookups.requesting.xoserveMeters,
      ofgemAverages: ({ ofgem_averages }) => ofgem_averages.averages
    })
  },
  watch: {
    addressFound(newValue) {
      if (newValue && this.quote.address) {
        this.$v.address.$touch();
        this.onChoose(this.quote.address);
        this.setDefaultUsage();
      }
    }
  },
  methods: {
    ...mapActions({
      getAddresses: 'lookups/getAddresses',
      getXoserveMeters: 'lookups/getXoserveMeters',
      resetList: 'lookups/resetList',
      getSuppliers: 'lookups/getSuppliers'
    }),
    onReset() {
      this.quote.address = {};
      this.selectedAddress = null;
      this.quote.postcode = this.quote.postcode.toUpperCase();
    },
    onConfirmGdpr() {
      this.quote.gdprConsent = true;
      this.getAddresses(this.quote.postcode);
    },
    onGetAddress(postcode) {
      this.$refs.address.onReset();
      this.$v.postcode.$touch();

      if (this.$v.postcode.$invalid) return;

      this.$v.$reset();

      this.getAddresses(postcode);
    },
    async onChoose(selected) {
      if (process.env.VUE_APP_GB_DATA == 'true') {
        this.selectedAddress = selected.text;
        this.quote.address = selected;

        const { mpan, mprn } = selected.parts;

        this.noMpanOrMprn = !mpan || !mprn;
        this.$v.address.$reset();
      } else {
        this.selectedAddress = selected.text;
        const xoserveMeters = await this.getXoserveMeters(selected);
        const { mpan, mprn } = xoserveMeters;

        this.quote.address = {
          parts: { ...selected, ...xoserveMeters },
          text: selected.text
        };

        this.setDefaults();
        if (this.isQuoteParamsJourney == true) {
          this.setDefaultUsage();
        }

        this.noMpanOrMprn = !mpan || !mprn;
        this.$v.address.$reset();
      }
    },
    setDefaults() {
      let { gasdetails, elecdetails } = this.quote.address.parts;

      gasdetails = gasdetails.find(({ isDomestic }) => isDomestic) || {};
      elecdetails = elecdetails.find(({ isDomestic }) => isDomestic) || {};

      const twoSuppliers = gasdetails.mugSupplierId != null && elecdetails.mugSupplierId != null;
      this.quote.supplierId = gasdetails.mugSupplierId || elecdetails.mugSupplierId;
      this.quote.gasUsage.usage = gasdetails.mpaq;
      this.quote.gasUsage.usageType = 1;
      this.quote.gasUsage.usageUnit = 1;
      this.quote.electricityUsage.usageType = 1;
      this.quote.electricityUsage.usageUnit = 1;
      this.quote.currentPayMethod = 'monthlyDirectDebit';
      this.quote.electricityCurrentPayMethod = 'monthlyDirectDebit';
      this.quote.gasCurrentPayMethod = 'monthlyDirectDebit';
      this.quote.gasSupplierId = gasdetails.mugSupplierId;
      this.quote.electricitySupplierId = elecdetails.mugSupplierId;
      this.quote.electricitySmartMeterInstalled = elecdetails.isSmartMeter;
      this.quote.gasSmartMeterInstalled = gasdetails.isSmartMeter;
      this.quote.newTariffPaymentMethod = 'monthlyDirectDebit';
      this.quote.sameSupplier = gasdetails.mugSupplierId == elecdetails.mugSupplierId ? 'yes' : 'no';
      this.quote.electricityUsage.disabledUnits = false;
      this.quote.heatingFuel = null;
      this.quote.cookingFuel = null;

      if (twoSuppliers) {
        this.quote.compareFuel = 'gasElectricity';
        this.quote.address.parts.fuelType = 'gasElectricity';
      } else {
        this.quote.compareFuel = gasdetails.mugSupplierId ? 'gas' : 'electricity';
        this.quote.address.parts.fuelType = gasdetails.mugSupplierId ? 'gas' : 'electricity';
      }

      if (this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'yes') {
        if (elecdetails.isPrepayment == true && gasdetails.isPrepayment == true) {
          this.quote.currentPayMethod = 'prePaymentMeter';
        }
      }

      if ((this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'no') || this.quote.compareFuel == 'gas') {
        if (gasdetails.isPrepayment == true) {
          this.quote.gasCurrentPayMethod = 'prePaymentMeter';
        }
        this.quote.electricityUsage.disabledUnits = true;
      }

      if ((this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'no') || this.quote.compareFuel == 'electricity') {
        if (elecdetails.isPrepayment == true) {
          this.quote.electricityCurrentPayMethod = 'prePaymentMeter';
        }
      }

      if (this.quote.compareFuel == 'electricity') {
        this.quote.heatingFuel = 2;
        this.quote.cookingFuel = 2;

        return this.getSuppliers('electricity');
      }

      if (this.quote.compareFuel == 'gas') {
        return this.getSuppliers('gas');
      }

      if (this.quote.compareFuel == 'gasElectricity' && this.quote.sameSupplier == 'yes') {
        this.quote.electricityUsage.disabledUnits = true;
        this.getSuppliers(this.quote.compareFuel);
      } else {
        this.getSuppliers('gas');
        this.getSuppliers('electricity');
      }

      if (this.isQuoteParamsJourney == true) {
        if (this.quote.compareFuel == 'gasElectricity') {
          this.quote.supplierId = gasdetails.mugSupplierId || elecdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.gasSupplierId = gasdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.electricitySupplierId = elecdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.address.parts.gasdetails.mugSupplierId = this.quote.address.parts.gasdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.address.parts.elecdetails.mugSupplierId = this.quote.address.parts.elecdetails.mugSupplierId || this.defaultSupplierId;
        }

        if (this.quote.compareFuel == 'gas') {
          this.quote.supplierId = gasdetails.mugSupplierId || elecdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.gasSupplierId = gasdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.address.parts.gasdetails.mugSupplierId = this.quote.address.parts.gasdetails.mugSupplierId || this.defaultSupplierId;
        }

        if (this.quote.compareFuel == 'electricity') {
          this.quote.supplierId = elecdetails.mugSupplierId || elecdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.electricitySupplierId = elecdetails.mugSupplierId || this.defaultSupplierId;
          this.quote.address.parts.elecdetails.mugSupplierId = this.quote.address.parts.elecdetails.mugSupplierId || this.defaultSupplierId;
        }
      }
    },
    setDefaultUsage() {
      if (this.$route.query.propertyType) {
        const propertyType = this.$route.query.propertyType.split(" ")
          .join("").toLowerCase();
        this.ofgemAverageGasList = this.ofgemAverageList('gas');
        this.ofgemAverageElecList = this.ofgemAverageList('elec');

        if (propertyType === 'flat') {
          // 'Low Usage'
          this.quote.electricityUsage.usage = this.findUsageValue('elec', 'Low Usage');
        } else if (propertyType === 'semidetached' || propertyType === 'terrace') {
          // 'Medium Usage'
          this.quote.electricityUsage.usage = this.findUsageValue('elec', 'Medium Usage')
        } else {
          // 'High Usage' - bungalow and detached
          this.quote.electricityUsage.usage = this.findUsageValue('elec', 'High Usage');
        }
      }
    },
    ofgemAverageList(fuel) {
      if (fuel == 'gas') {
        return this.ofgemAverages.gas;
      }

      if (this.is02Meter) {
        return this.ofgemAverages.electricity02;
      }

      return this.ofgemAverages.electricity01;
    },
    findUsageValue(type, value) {
      if (type == 'gas') {
        return this.ofgemAverageGasList.find(usage => usage.label == value)?.value;
      }

      return this.ofgemAverageElecList.find(usage => usage.label == value)?.value;
    },
  },
};
</script>

<style lang="scss"></style>
